import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import ExplorerLayout from '../../components/shell/ExplorerTree';
import { useCourseExplorer } from '../../components/courses/CourseExplorer';
import { useTabLabel } from '../../components/shell/TabsContext';
import RecordPage from '../../components/shell/RecordPage';
import { stripHtml } from '../../lib/htmlText';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { PaneFooter } from '../../components/shell/SidePane';
import DataGrid, { StatusBadge } from '../../components/datagrid/DataGrid';
import {
  Users,
  Books,
  Pencil,
  TreeStructure,
  ChartBar,
  Clock,
  CalendarBlank,
  Trophy,
  Eye,
  CheckCircle,
  XCircle,
  EyeSlash,
  Exam,
  Certificate,
  TrendUp,
  TrendDown,
  Minus,
  ArrowCounterClockwise
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { openLearnerPreview } from '../../components/PreviewMode';

// Helper to format seconds to readable time
const formatTime = (seconds) => {
  if (!seconds || seconds === 0) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m`;
  } else {
    return `<1m`;
  }
};

export default function CourseView() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const explorer = useCourseExplorer({ api, selectedKey: `course:${courseId}`, storageKey: 'courses' });
  
  const [course, setCourse] = useState(null);
  useTabLabel(course?.title ? `View: ${course.title}` : null);
  const [stats, setStats] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Reset progress dialog state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [resetType, setResetType] = useState('status_only');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchCourseData();
  }, [courseId]);

  const togglePublish = async () => {
    try {
      await api.put(`/courses/${courseId}`, { is_published: !course.is_published });
      toast.success(course.is_published ? 'Course unpublished' : 'Course published');
      fetchCourseData();
    } catch (error) {
      toast.error('Failed to update course');
    }
  };

  const fetchCourseData = async () => {
    try {
      const [courseRes, statsRes, enrollmentsRes] = await Promise.all([
        api.get(`/courses/${courseId}`),
        api.get(`/courses/${courseId}/stats`).catch(() => ({ data: null })),
        api.get(`/courses/${courseId}/enrollments`).catch(() => ({ data: [] }))
      ]);
      
      setCourse(courseRes.data);
      setStats(statsRes.data);
      setEnrollments(enrollmentsRes.data || []);
    } catch (error) {
      toast.error('Failed to load course');
      navigate('/instructor/courses');
    } finally {
      setLoading(false);
    }
  };

  const handleResetProgress = async () => {
    if (!selectedStudent) return;
    
    setResetting(true);
    try {
      await api.post(`/courses/${courseId}/students/${selectedStudent.user_id}/reset-progress`, {
        reset_type: resetType
      });
      toast.success(resetType === 'full_reset' 
        ? 'Student progress fully reset' 
        : 'Student status changed to in-progress'
      );
      setResetDialogOpen(false);
      setSelectedStudent(null);
      fetchCourseData(); // Refresh data
    } catch (error) {
      toast.error('Failed to reset progress');
    } finally {
      setResetting(false);
    }
  };

  const openResetDialog = (enrollment) => {
    setSelectedStudent(enrollment);
    setResetType('status_only');
    setResetDialogOpen(true);
  };

  if (loading || !course) {
    return (
      <DashboardLayout>
        <RecordPage title={loading ? 'Course' : 'Course not found'} loading={loading} onBack={() => navigate('/instructor/courses')}>
          <p className="text-muted-foreground text-center py-12">Course not found</p>
        </RecordPage>
      </DashboardLayout>
    );
  }

  const completionRate = stats?.completion_rate || 0;
  const avgProgress = stats?.avg_progress || 0;
  const totalEnrollments = stats?.total_enrollments || enrollments.length || 0;
  const activeStudents = stats?.active_students || enrollments.filter(e => e.status === 'active').length || 0;
  const completedStudents = stats?.completed_students || enrollments.filter(e => e.completed).length || 0;
  const avgExamScore = stats?.avg_exam_score || 0;

  // ─── Side pane: reset progress (choice of reset type + confirm) ───
  const closeResetPane = () => { setResetDialogOpen(false); setSelectedStudent(null); };
  const side = resetDialogOpen ? {
    open: true,
    onOpenChange: closeResetPane,
    title: 'Reset student progress',
    subtitle: `Choose how to reset progress for ${selectedStudent?.user_name || 'this student'}`,
    icon: ArrowCounterClockwise,
    testid: 'reset-progress-pane',
    children: (
      <div className="space-y-4" data-testid="reset-progress-options">
        <div 
          className={`p-4 border rounded-lg cursor-pointer transition-colors ${resetType === 'status_only' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
          onClick={() => setResetType('status_only')}
        >
          <div className="flex items-start gap-3">
            <input 
              type="radio" 
              checked={resetType === 'status_only'} 
              onChange={() => setResetType('status_only')}
              className="mt-1"
            />
            <div>
              <p className="font-medium">Change Status Only</p>
              <p className="text-sm text-muted-foreground">
                Mark as "In Progress" but keep all their lesson progress and time data intact.
                Use this if you just want to allow them to retake the certificate.
              </p>
            </div>
          </div>
        </div>
        
        <div 
          className={`p-4 border rounded-lg cursor-pointer transition-colors ${resetType === 'full_reset' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
          onClick={() => setResetType('full_reset')}
        >
          <div className="flex items-start gap-3">
            <input 
              type="radio" 
              checked={resetType === 'full_reset'} 
              onChange={() => setResetType('full_reset')}
              className="mt-1"
            />
            <div>
              <p className="font-medium text-red-600">Full Reset</p>
              <p className="text-sm text-muted-foreground">
                Delete all progress, time spent data, and certificate. Student will start from scratch.
                <span className="text-red-500 font-medium"> This cannot be undone.</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    ),
    footer: (
      <PaneFooter
        onCancel={closeResetPane}
        onSubmit={handleResetProgress}
        submitLabel={resetting ? 'Resetting...' : 'Reset progress'}
        submitting={resetting}
        submitVariant="destructive"
        submitIcon={ArrowCounterClockwise}
      />
    ),
  } : null;

  return (
    <DashboardLayout>
      <ExplorerLayout explorer={explorer.props}>
      <RecordPage
        data-testid="course-view-page"
        title={course.title}
        subtitle={[course.course_code, course.category, stripHtml(course.description, 120)].filter(Boolean).join(' · ')}
        badge={<StatusBadge value={course.is_published ? 'published' : 'draft'} />}
        onBack={() => navigate('/instructor/courses')}
        onRefresh={fetchCourseData}
        maxWidth="none"
        side={side}
        primary={[
          { key: 'edit', label: 'Edit', icon: Pencil, onClick: () => navigate(`/instructor/courses/${courseId}/edit`), testid: 'edit-course-btn' },
          { key: 'structure', label: 'Structure', icon: TreeStructure, variant: 'outline', onClick: () => navigate(`/instructor/courses/${courseId}/structure`) },
          { key: 'preview', label: 'Preview', icon: Eye, variant: 'outline', onClick: () => openLearnerPreview(`/course/${courseId}`) },
        ]}
        secondary={[
          { key: 'publish', label: course.is_published ? 'Unpublish' : 'Publish', icon: course.is_published ? EyeSlash : Eye, onClick: togglePublish },
        ]}
      >
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{totalEnrollments}</p>
                    <p className="text-xs text-muted-foreground">Total Enrolled</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{activeStudents}</p>
                    <p className="text-xs text-muted-foreground">Active Students</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                    <Trophy className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{completedStudents}</p>
                    <p className="text-xs text-muted-foreground">Completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <ChartBar className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{completionRate.toFixed(0)}%</p>
                    <p className="text-xs text-muted-foreground">Completion Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                    <TrendUp className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{avgProgress.toFixed(0)}%</p>
                    <p className="text-xs text-muted-foreground">Avg Progress</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <Exam className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{avgExamScore > 0 ? `${avgExamScore.toFixed(0)}%` : '-'}</p>
                    <p className="text-xs text-muted-foreground">Avg Exam Score</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Tabs for detailed info */}
        <Tabs defaultValue="students" className="space-y-4">
          <TabsList>
            <TabsTrigger value="students" className="gap-2">
              <Users className="w-4 h-4" />
              Students ({totalEnrollments})
            </TabsTrigger>
            <TabsTrigger value="content" className="gap-2">
              <Books className="w-4 h-4" />
              Content
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2">
              <Clock className="w-4 h-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="students">
            <DataGrid
              title="Enrolled students"
              rows={enrollments}
              loading={loading}
              onRefresh={fetchCourseData}
              searchKeys={['user_name', 'user_email']}
              columns={[
                {
                  key: 'user_name', header: 'Student', accessor: (e) => e.user_name || e.user_email || '',
                  render: (e) => (
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-shell-primary/10 text-shell-primary flex items-center justify-center text-[11px] font-semibold">
                        {e.user_name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <span className="font-medium">{e.user_name || e.user_email}</span>
                    </div>
                  ),
                },
                { key: 'user_email', header: 'Email', accessor: (e) => e.user_email || '', className: 'text-muted-foreground' },
                { key: 'enrolled_at', header: 'Enrolled', width: 120, render: (e) => (e.enrolled_at ? new Date(e.enrolled_at).toLocaleDateString() : '—') },
                {
                  key: 'status', header: 'Status', width: 130, accessor: (e) => e.status || '',
                  render: (e) => (
                    e.status === 'completed' ? <StatusBadge value="completed" />
                      : (e.status === 'active' || e.status === 'in_progress') ? <StatusBadge value="in_progress" />
                        : e.status === 'enrolled' ? <StatusBadge value="not_started" />
                          : <StatusBadge value={e.status} />
                  ),
                },
                {
                  key: 'progress', header: 'Progress', width: 160, accessor: (e) => e.progress || 0,
                  render: (e) => (
                    <div className="flex items-center gap-2">
                      <Progress value={e.progress || 0} className="w-20 h-1.5" />
                      <span className="text-xs font-medium tabular-nums">{(e.progress || 0).toFixed(0)}%</span>
                    </div>
                  ),
                },
                { key: 'total_time_spent_seconds', header: 'Time spent', width: 110, align: 'right', accessor: (e) => e.total_time_spent_seconds || 0, render: (e) => formatTime(e.total_time_spent_seconds), className: 'tabular-nums' },
              ]}
              rowTone={(e) => (e.status === 'completed' ? 'success' : null)}
              onRowDoubleClick={(e) => navigate(`/instructor/courses/${courseId}/students/${e.user_id}/analytics`)}
              selectionActions={[
                { key: 'analytics', label: 'Analytics', icon: ChartBar, onClick: ([e]) => navigate(`/instructor/courses/${courseId}/students/${e.user_id}/analytics`) },
                { key: 'reset', label: 'Reset progress', icon: ArrowCounterClockwise, onClick: ([e]) => openResetDialog(e) },
              ]}
              emptyMessage="No students enrolled yet. Share this course to get students enrolled."
            />
          </TabsContent>

          <TabsContent value="content">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Course Content</CardTitle>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/instructor/courses/${courseId}/structure`}>
                    <TreeStructure className="w-4 h-4 mr-2" />
                    Edit Structure
                  </Link>
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <p className="text-3xl font-bold">{course.node_count || 0}</p>
                    <p className="text-sm text-muted-foreground">Total Nodes</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <p className="text-3xl font-bold">{course.exam_count || 0}</p>
                    <p className="text-sm text-muted-foreground">Exams</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <p className="text-3xl font-bold">{course.video_count || 0}</p>
                    <p className="text-sm text-muted-foreground">Videos</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <p className="text-3xl font-bold">{course.duration_hours || '-'}</p>
                    <p className="text-sm text-muted-foreground">Hours</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Course Settings</CardTitle>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/instructor/courses/${courseId}/edit`}>
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit Settings
                  </Link>
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">Pace Control</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {course.control_student_pace ? (
                        <span className="text-emerald-600 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" /> Enabled - Students must complete lessons in order
                        </span>
                      ) : (
                        <span className="text-muted-foreground flex items-center gap-1">
                          <XCircle className="w-4 h-4" /> Disabled - Students can access any lesson
                        </span>
                      )}
                    </p>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <CalendarBlank className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">Availability</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {course.availability_type === 'open' && 'Always available'}
                      {course.availability_type === 'date_range' && (
                        <>Date range: {course.availability_start_date} - {course.availability_end_date}</>
                      )}
                      {course.availability_type === 'days_after_enrollment' && (
                        <>{course.availability_days} days after enrollment</>
                      )}
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Certificate className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">Certificates</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {course.enable_certificate ? (
                        <span className="text-emerald-600 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" /> Enabled
                        </span>
                      ) : (
                        <span className="text-muted-foreground flex items-center gap-1">
                          <XCircle className="w-4 h-4" /> Disabled
                        </span>
                      )}
                    </p>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <CalendarBlank className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">Created</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {new Date(course.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

      </RecordPage>
      </ExplorerLayout>
    </DashboardLayout>
  );
}
